import React, { useState } from 'react';
import { cn } from '@/lib/utils';

interface InterlacedLogoV2Props {
  /**
   * Variante do logo
   * @default 'horizontal'
   */
  variant?: 'horizontal' | 'symbol' | 'vertical';
  /**
   * Tamanho do logo
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * Classes CSS adicionais
   */
  className?: string;
  /**
   * Texto alternativo para acessibilidade
   */
  alt?: string;
  /**
   * Elemento HTML a ser renderizado (para SEO)
   * @default 'span'
   */
  as?: 'h1' | 'h2' | 'h3' | 'span' | 'div';
  /**
   * Se true, carrega imagem com prioridade alta (para LCP)
   * @default false
   */
  priority?: boolean;
}

const sizeMap = {
  horizontal: {
    sm: 'h-6',
    md: 'h-8',
    lg: 'h-10',
    xl: 'h-12',
  },
  symbol: {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16',
  },
  vertical: {
    sm: 'h-16',
    md: 'h-24',
    lg: 'h-32',
    xl: 'h-40',
  },
};

const imagePaths = {
  horizontal: '/logo/horizontal/logo-horizontal.png',
  symbol: '/logo/symbol/symbol-colored.png',
  vertical: '/logo/vertical/logo-vertical-colored.png',
};

/**
 * Componente de logo oficial da aplicação Interlaced (V2).
 * Suporta 3 variantes (horizontal, symbol, vertical) e 4 tamanhos.
 * 
 * @example
 * ```tsx
 * <InterlacedLogoV2 variant="horizontal" size="md" />
 * <InterlacedLogoV2 variant="symbol" size="lg" as="h1" />
 * ```
 */
const InterlacedLogoV2: React.FC<InterlacedLogoV2Props> = ({
  variant = 'horizontal',
  size = 'md',
  className,
  alt = 'Interlaced',
  as: Component = 'span',
  priority = false,
}) => {
  const [imageError, setImageError] = useState(false);

  // Fallback para texto se imagem não carregar
  if (imageError) {
    return (
      <Component className={cn('font-serif font-bold text-2xl', className)}>
        <span className="text-white">Inter</span>
        <span className="text-luxury-red">laced</span>
      </Component>
    );
  }

  return (
    <Component className={cn('inline-block', className)}>
      <img
        src={imagePaths[variant]}
        alt={alt}
        className={cn('object-contain', sizeMap[variant][size])}
        onError={() => setImageError(true)}
        loading={priority ? 'eager' : 'lazy'}
        {...(priority && { fetchpriority: 'high' })}
      />
    </Component>
  );
};

export default InterlacedLogoV2;
